import React, { useMemo, useState } from 'react';
import { Person, SelectedPerson } from '@/db/types/user';
import AuthLayout from './AuthLayout';
import { Button } from '@mes-amis/galette-design-system';
import { ExclamationTriangleIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface PersonSelectorProps {
  people: Person[];
  onPersonSelected: (person: SelectedPerson) => void;
  isLoading?: boolean;
}

export default function PersonSelector({ people, onPersonSelected, isLoading = false }: PersonSelectorProps) {
  const [selectedPersonId, setSelectedPersonId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredPeople = useMemo(() => {
    if (!searchQuery.trim()) return people;
    const q = searchQuery.toLowerCase();
    return people.filter(person =>
      person.organization.name.toLowerCase().includes(q)
    );
  }, [people, searchQuery]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedPersonId === null) {
      return;
    }

    const selectedPerson = people.find(person => person.id === selectedPersonId);
    if (selectedPerson) {
      onPersonSelected(selectedPerson);
    }
  };

  if (people.length === 0) {
    return (
      <AuthLayout>
        <div className="text-center flex flex-col items-center gap-4">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-400" />
          <h1 className="font-heading text-[28px] leading-8 text-black">
            No Organizations Available
          </h1>
          <p className="text-sm text-[#555]">
            Your account is not associated with any organizations. Please contact your administrator.
          </p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      {/* Heading */}
      <div className="flex flex-col gap-4">
        <h1 className="font-heading text-[28px] leading-8 text-black">
          Select Organization
        </h1>
        <p className="text-sm leading-5 text-black">
          Choose which organization you want to access
        </p>
      </div>

      {/* Organization list */}
      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        {people.length > 5 && (
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search organizations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="block w-full pl-10 pr-10 py-2 border border-gray-200 rounded-lg text-sm bg-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-deep-sea-green focus:border-deep-sea-green"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            )}
          </div>
        )}
        <div className="space-y-3 max-h-64 overflow-y-auto">
          {filteredPeople.length === 0 && searchQuery ? (
            <p className="text-sm text-gray-500 text-center py-4">No organizations match your search</p>
          ) : filteredPeople.map((person) => (
            <label
              key={person.id}
              htmlFor={`person-${person.id}`}
              className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-colors ${
                selectedPersonId === person.id
                  ? 'border-deep-sea-green bg-deep-sea-green/5'
                  : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <input
                id={`person-${person.id}`}
                name="person"
                type="radio"
                value={person.id}
                checked={selectedPersonId === person.id}
                onChange={(e) => setSelectedPersonId(parseInt(e.target.value))}
                className="h-4 w-4 text-deep-sea-green focus:ring-deep-sea-green border-gray-300"
              />
              <span className="text-sm font-medium text-gray-900">{person.organization.name}</span>
            </label>
          ))}
        </div>

        <Button
          variant="primary"
          type="submit"
          disabled={selectedPersonId === null || isLoading}
          className="w-full"
          size="md"
        >
          {isLoading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
              Processing...
            </>
          ) : (
            'Continue'
          )}
        </Button>
      </form>
    </AuthLayout>
  );
}
